import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errorText } from '../../lib/apiErrors';
import useLeadPipelineConfig from '../../hooks/useLeadPipelineConfig';
import { UTM_KEYS, trimUtmValue, buildUtmChanges } from '../../utils/utmUtils';
import RemoveFromPipelineDialog, { NOT_IN_PIPELINE, leadName } from './RemoveFromPipelineDialog';

// Re-export for backward compatibility — normalizeContactPayload and the test
// suite previously imported UTM_KEYS / trimUtmValue from this component.
// The canonical home is now utils/utmUtils.js (fixes inverted dependency).
export { UTM_KEYS, trimUtmValue };

const API = process.env.REACT_APP_BACKEND_URL || '';

// ─── Lead Pipeline + funnel fields, surfaced on ContactDetail.
// Dual-mode by design (issue #511):
//   * Page edit flow (editing=true AND editForm/setEditForm present): every
//     human-editable field renders a read-only value until the page enters
//     edit mode; changes write into the editForm draft (setEditForm) and are
//     committed by ContactDetail.handleSave, which dispatches to the right
//     endpoint (generic PUT for simple fields, dedicated endpoints for
//     stage / coach / sales-outcome / owner / lead-status).
//   * Standalone instant mode (Lead Pipeline working drawer passes
//     editing={true} + instant={true} with no editForm): legacy behavior —
//     generic PUT for simple fields, dedicated endpoints for stage / coach /
//     sales-outcome. `instant` makes this path explicit; a dev-time warning
//     fires when editing=true arrives without a draft and without `instant`.
// Built ALONGSIDE the Deal pipeline — this writes to the Contact/lead record.

const BAND_CLS = { green: 'bg-emerald-100 text-emerald-700', amber: 'bg-amber-100 text-amber-700', red: 'bg-red-100 text-red-700' };
const BIC_CLS = { action_needed: 'bg-amber-100 text-amber-700', waiting_on_lead: 'bg-blue-100 text-blue-700', na: 'bg-stone-100 text-stone-500' };
const SALES_OUTCOMES = ['Call Completed', 'No Show'];

const UTM_MAX = 200;

const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const selectCls = "mt-0.5 w-full text-sm text-stone-800 bg-white border border-stone-200 hover:border-stone-300 focus:border-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900/10 rounded-md px-2 py-1.5";
const inputCls = selectCls;

function DirtyLabel({ label, dirty }) {
    return (
        <>
            {label}
            {dirty && <span className="text-amber-500 ml-0.5" title="Unsaved change">●</span>}
        </>
    );
}

function ReadValue({ value }) {
    if (value != null && value !== '') {
        return <p className="text-sm font-medium break-words text-stone-800">{value}</p>;
    }
    return <span className="text-sm text-stone-400">—</span>;
}

function Card({ title, children }) {
    return (
        <div className="bg-white rounded-xl border border-stone-200">
            <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-stone-100">
                <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
            </div>
            <div className="p-4 sm:p-5">{children}</div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <p className="text-xs text-stone-400 mb-0.5">{label}</p>
            {children}
        </div>
    );
}

function SelectField({ label, value, options, onSave, disabled, editing = false, onDraftChange, dirty, testid }) {
    if (!editing) {
        return (
            <Field label={label}>
                <ReadValue value={value} />
            </Field>
        );
    }
    const opts = [...options];
    if (value && !opts.includes(value)) opts.unshift(value);
    return (
        <Field label={<DirtyLabel label={label} dirty={dirty} />}>
            <select className={selectCls} value={value || ''} disabled={disabled} data-testid={testid}
                onChange={e => (onDraftChange || onSave)(e.target.value || null)}>
                <option value="">(unset)</option>
                {opts.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
        </Field>
    );
}

function TextField({ label, value, onSave, type = 'text', disabled, placeholder, maxLength, editing = false, editValue, onDraftChange, dirty, testid }) {
    const [draft, setDraft] = useState(value ?? '');
    useEffect(() => { setDraft(value ?? ''); }, [value]);
    if (!editing) {
        return (
            <Field label={label}>
                <ReadValue value={value} />
            </Field>
        );
    }
    if (onDraftChange) {
        return (
            <Field label={<DirtyLabel label={label} dirty={dirty} />}>
                <input type={type} className={inputCls} value={editValue ?? ''} disabled={disabled} placeholder={placeholder} maxLength={maxLength} data-testid={testid}
                    onChange={e => onDraftChange(e.target.value === '' ? null : e.target.value)} />
            </Field>
        );
    }
    
    const commit = () => {
        const norm = draft === '' ? null : (type === 'number' ? Number(draft) : draft);
        const cur = value ?? (type === 'number' ? null : '');
        if (String(norm ?? '') !== String(cur ?? '')) onSave(norm);
    };
    return (
        <Field label={label}>
            <input type={type} className={inputCls} value={draft} disabled={disabled} placeholder={placeholder} maxLength={maxLength} data-testid={testid}
                onChange={e => setDraft(e.target.value)} onBlur={commit}
                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} />
        </Field>
    );
}

// Pure helper — exported for unit testing. Returns the provenance caption
// shown under the UTM Acquisition card based on whether any UTM fields hold
// a value, whether the contact has a funnel `source` (captured vs manual),
// and whether the card is currently editable (contact edit mode).
export function getUtmCaption(utmHasValues, utmFromFunnel, editing = true) {
    if (!utmHasValues) return 'No attribution recorded. Enter where this contact came from.';
    if (utmFromFunnel) return editing
        ? "Captured automatically from the funnel link. Captured values are protected; empty fields can be entered manually \u2014 click Save to commit."
        : "Captured automatically from the funnel link. Captured values are protected.";
    return editing
        ? "Entered manually. Filled values are protected; empty fields remain editable \u2014 click Save to commit."
        : "Entered manually. Filled values are protected.";
}

export function UtmField({ label, value, editing = true, draft, onDraftChange, disabled }) {
    if (value) {
        return (
            <Field label={label}>
                <p className="text-sm text-stone-800 font-medium break-words">{value}</p>
            </Field>
        );
    }
    if (!editing) {
        return (
            <Field label={label}>
                <span className="text-sm text-stone-400">—</span>
            </Field>
        );
    }
    return (
        <Field label={label}>
            <input type="text" className={inputCls} value={draft ?? ''} disabled={disabled} maxLength={UTM_MAX}
                placeholder="Enter manually"
                onChange={e => onDraftChange(e.target.value)} />
        </Field>
    );
}

function Badge({ children, cls }) {
    return <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${cls || 'bg-stone-100 text-stone-600'}`}>{children}</span>;
}

export default function LeadPipelineCard({ contact, contactId, teamMembers = [], getAuthHeader, onChange, editing = false, editForm, setEditForm, saving = false, isFieldDirty, instant = false, onStageCleared, readOnlyReason = null }) {
    const { config } = useLeadPipelineConfig();
    const [busy, setBusy] = useState(false);
    // #905: "Not in pipeline" in instant (drawer) mode asks first. The page edit
    // flow just drafts null — its Save is the confirmation.
    const [confirmClear, setConfirmClear] = useState(false);
    const [products, setProducts] = useState([]);
    const [utmDrafts, setUtmDrafts] = useState({});

    // Dual-mode contract (#511): editing=true with editForm/setEditForm is the
    // page draft flow; editing=true WITHOUT them is the legacy instant-write
    // drawer. That second path is now explicit via `instant` — a caller passing
    // editing=true with no draft and no `instant` is a contract violation, so
    // flag it in dev instead of silently committing instant writes.
    if (editing && !editForm && !setEditForm && !instant && process.env.NODE_ENV !== 'production') {
        console.warn(
            '[LeadPipelineCard] editing=true passed without editForm/setEditForm. ' +
            'This enables instant-write (drawer) mode. Pass instant={true} to make it explicit.'
        );
    }

    const stages = config.stages || [];
    const opt = (k) => config[k] || [];

    useEffect(() => {
        // Product picker for "Product Sold" (graceful if unavailable).
        axios.get(`${API}/api/products/picker`, { headers: getAuthHeader() })
            .then(res => setProducts(Array.isArray(res.data) ? res.data : (res.data?.data || [])))
            .catch(() => setProducts([]));
    }, [getAuthHeader]);

    const run = useCallback(async (fn) => {
        if (readOnlyReason) { toast.info(readOnlyReason); return; }
        setBusy(true);
        try { await fn(); if (onChange) await onChange(); }
        catch (err) { toast.error(errorText(err, 'Update failed')); }
        finally { setBusy(false); }
    }, [onChange, readOnlyReason]);

    const patch = (fields) => run(() => axios.put(`${API}/api/contacts/${contactId}`, fields, { headers: getAuthHeader() }));
    const setStage = (stageKey) => run(() => axios.put(`${API}/api/contacts/${contactId}/pipeline-stage`, { stage: stageKey }, { headers: getAuthHeader() }));
    const setCoach = (userId) => run(() => axios.put(`${API}/api/contacts/${contactId}/assign-coach`, { coach_user_id: userId || null }, { headers: getAuthHeader() }));
    const setOutcome = (outcome) => run(() => axios.put(`${API}/api/contacts/${contactId}/sales-outcome`, { outcome: outcome || null }, { headers: getAuthHeader() }));

    const onProductSold = (val) => {
        if (val === '__custom__') { patch({ product_sold_id: null, product_sold_name: 'Custom' }); return; }
        const p = products.find(x => (x.product_id || x.id) === val);
        if (!p) { patch({ product_sold_id: null, product_sold_name: null, product_value: null, product_value_currency: null }); return; }
        patch({
            product_sold_id: p.product_id || p.id,
            product_sold_name: p.name,
            product_value: p.amount ?? p.price ?? null,
            product_value_currency: p.currency || null,
        });
    };

    const c = contact || {};

    const editingFields = !!(editing && !readOnlyReason && editForm && setEditForm);
    const ev = (k) => (editingFields ? editForm[k] : c[k]);
    const setKF = (k, v) => setEditForm(p => ({ ...(p || {}), [k]: v }));
    const isDirty = (k) => !!(editingFields && isFieldDirty && isFieldDirty(k));

    const draftProductSold = (val) => {
        if (val === '__custom__') { setEditForm(p => ({ ...(p || {}), product_sold_id: null, product_sold_name: 'Custom' })); return; }
        const p = products.find(x => (x.product_id || x.id) === val);
        if (!p) { setEditForm(p => ({ ...(p || {}), product_sold_id: null, product_sold_name: null, product_value: null, product_value_currency: null })); return; }
        setEditForm(p => ({ ...(p || {}),
            product_sold_id: p.product_id || p.id,
            product_sold_name: p.name,
            product_value: p.amount ?? p.price ?? null,
            product_value_currency: p.currency || null,
        }));
    };

    const utmHasValues = UTM_KEYS.some(k => c[k]);
    const utmFromFunnel = !!c.source;
    const utmCaption = getUtmCaption(utmHasValues, utmFromFunnel, editing && !readOnlyReason);
    const utmDirty = !editForm && Object.keys(buildUtmChanges(utmDrafts, c)).length > 0;
    const saveUtms = async () => {
        if (editForm) return;
        const changes = buildUtmChanges(utmDrafts, c);
        if (Object.keys(changes).length === 0) return;
        await patch(changes);
        setUtmDrafts({});
    };
    const stageKeyOf = (v) => (stages.find(s => s.key === v || s.label === v) || {}).key || v || '';
    const stageLabelOf = (v) => (stages.find(s => s.key === stageKeyOf(v)) || {}).label || v || '';
    const coachMatch = teamMembers.find(m => m.user_id === c.coach_assigned_user_id);
    const coachName = coachMatch ? (coachMatch.name || coachMatch.email) : '';

    return (
        <div className="space-y-6">
            {readOnlyReason && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" role="note">{readOnlyReason}</div>}
            <RemoveFromPipelineDialog
                open={confirmClear}
                contactId={contactId}
                name={leadName(c)}
                getAuthHeader={getAuthHeader}
                onCancel={() => setConfirmClear(false)}
                onRemoved={async (id) => {
                    setConfirmClear(false);
                    if (onStageCleared) onStageCleared(id);
                    else if (onChange) await onChange();
                }}
            />
            {/* ── Lead Pipeline ────────────────────────────────────────────── */}
            <Card title="Lead Pipeline">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
                    {/* Stage */}
                    <Field label={<DirtyLabel label="Pipeline Stage" dirty={isDirty('pipeline_stage')} />}>
                        {!(editing && !readOnlyReason) ? (
                            <ReadValue value={stageLabelOf(c.pipeline_stage)} />
                        ) : (
                            <select className={selectCls} value={stageKeyOf(ev('pipeline_stage'))} disabled={busy || !!readOnlyReason} data-testid="lp-stage"
                                onChange={e => {
                                    if (editingFields) setKF('pipeline_stage', e.target.value || null);
                                    else if (e.target.value) setStage(e.target.value);
                                    else if (c.pipeline_stage) setConfirmClear(true);
                                }}>
                                <option value="">{NOT_IN_PIPELINE}</option>
                                {stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                            </select>
                        )}
                    </Field>
                    {/* Time in stage (computed, read-only) */}
                    <Field label="Time in Stage">
                        {c.time_in_stage_days != null
                            ? <Badge cls={BAND_CLS[c.time_in_stage_band]}>{c.time_in_stage_days} day{c.time_in_stage_days === 1 ? '' : 's'}</Badge>
                            : <span className="text-sm text-stone-400">—</span>}
                    </Field>
                    {/* Ball in court (computed, read-only) */}
                    <Field label="Ball in Court">
                        {c.ball_in_court
                            ? <Badge cls={BIC_CLS[c.ball_in_court]}>{c.ball_in_court_label || humanize(c.ball_in_court)}</Badge>
                            : <span className="text-sm text-stone-400">—</span>}
                    </Field>
                    {/* Assigned Coach (reuses org team-member list; stores user_id) */}
                    <Field label={<DirtyLabel label="Assigned Coach" dirty={isDirty('coach_assigned_user_id')} />}>
                        {!(editing && !readOnlyReason) ? (
                            <ReadValue value={coachName} />
                        ) : (
                            <select className={selectCls} value={ev('coach_assigned_user_id') || ''} disabled={busy || !!readOnlyReason} data-testid="lp-coach"
                                onChange={e => editingFields ? setKF('coach_assigned_user_id', e.target.value || null) : setCoach(e.target.value)}>
                                <option value="">(unassigned)</option>
                                {teamMembers.map(m => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
                            </select>
                        )}
                    </Field>
                    <SelectField label="Prospect Source" editing={editing && !readOnlyReason} value={ev('prospect_source')} options={opt('prospect_source_options')} disabled={busy || !!readOnlyReason} dirty={isDirty('prospect_source')} onSave={v => patch({ prospect_source: v })} onDraftChange={editingFields ? v => setKF('prospect_source', v) : undefined} testid="lp-prospect-source" />
                    <SelectField label="Prospect Qualification" editing={editing && !readOnlyReason} value={ev('prospect_qualification')} options={opt('prospect_qualification_options')} disabled={busy || !!readOnlyReason} dirty={isDirty('prospect_qualification')} onSave={v => patch({ prospect_qualification: v })} onDraftChange={editingFields ? v => setKF('prospect_qualification', v) : undefined} />
                    <SelectField label="Prospect Status" editing={editing && !readOnlyReason} value={ev('prospect_status')} options={opt('prospect_status_options')} disabled={busy || !!readOnlyReason} dirty={isDirty('prospect_status')} onSave={v => patch({ prospect_status: v })} onDraftChange={editingFields ? v => setKF('prospect_status', v) : undefined} />
                    <Field label="Lead Status (system)">
                        {c.lead_status ? <Badge>{humanize(c.lead_status)}</Badge> : <span className="text-sm text-stone-400">—</span>}
                    </Field>
                    <div className="sm:col-span-2 xl:col-span-3">
                        <TextField label="Last Touchpoint" editing={editing && !readOnlyReason} value={ev('last_touchpoint')} editValue={ev('last_touchpoint')} disabled={busy || !!readOnlyReason} placeholder="e.g. Email received 3 days ago" dirty={isDirty('last_touchpoint')} onSave={v => patch({ last_touchpoint: v })} onDraftChange={editingFields ? v => setKF('last_touchpoint', v) : undefined} testid="lp-last-touchpoint" />
                    </div>
                </div>

                {/* Sales Call */}
                <div className="mt-5 pt-4 border-t border-stone-100">
                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Sales Call</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
                        <TextField label="Sales Call Date" type="date" editing={editing && !readOnlyReason} value={(ev('sales_call_date') || '').slice(0, 10)} editValue={(ev('sales_call_date') || '').slice(0, 10)} disabled={busy || !!readOnlyReason} dirty={isDirty('sales_call_date')} onSave={v => patch({ sales_call_date: v })} onDraftChange={editingFields ? v => setKF('sales_call_date', v) : undefined} testid="lp-call-date" />
                        <Field label={<DirtyLabel label="Sales Call Outcome" dirty={isDirty('sales_call_outcome')} />}>
                            {!(editing && !readOnlyReason) ? (
                                <ReadValue value={ev('sales_call_outcome')} />
                            ) : (
                                <select className={selectCls} value={ev('sales_call_outcome') || ''} disabled={busy || !!readOnlyReason} data-testid="lp-outcome"
                                    onChange={e => editingFields ? setKF('sales_call_outcome', e.target.value || null) : setOutcome(e.target.value)}>
                                    <option value="">(none)</option>
                                    {SALES_OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                            )}
                        </Field>
                        <TextField label="Call Count" type="number" editing={editing && !readOnlyReason} value={ev('call_count')} editValue={ev('call_count')} disabled={busy || !!readOnlyReason} dirty={isDirty('call_count')} onSave={v => patch({ call_count: v })} onDraftChange={editingFields ? v => setKF('call_count', v) : undefined} testid="lp-call-count" />
                    </div>
                </div>

                {/* Won */}
                <div className="mt-5 pt-4 border-t border-stone-100">
                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Won</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
                        {products.length > 0 ? (
                            <Field label={<DirtyLabel label="Product Sold" dirty={isDirty('product_sold_id') || isDirty('product_sold_name')} />}>
                                {!(editing && !readOnlyReason) ? (
                                    <ReadValue value={c.product_sold_name} />
                                ) : (
                                    <select className={selectCls} value={ev('product_sold_id') || (ev('product_sold_name') === 'Custom' ? '__custom__' : '')} disabled={busy || !!readOnlyReason} data-testid="lp-product"
                                        onChange={e => editingFields ? draftProductSold(e.target.value) : onProductSold(e.target.value)}>
                                        <option value="">(none)</option>
                                        {products.map(p => <option key={p.product_id || p.id} value={p.product_id || p.id}>{p.name}</option>)}
                                        <option value="__custom__">Custom…</option>
                                    </select>
                                )}
                            </Field>
                        ) : (
                            <TextField label="Product Sold" editing={editing && !readOnlyReason} value={ev('product_sold_name')} editValue={ev('product_sold_name')} disabled={busy || !!readOnlyReason} dirty={isDirty('product_sold_name')} onSave={v => patch({ product_sold_name: v })} onDraftChange={editingFields ? v => setKF('product_sold_name', v) : undefined} />
                        )}
                        <TextField label="Product Value" type="number" editing={editing && !readOnlyReason} value={ev('product_value')} editValue={ev('product_value')} disabled={busy || !!readOnlyReason} dirty={isDirty('product_value')} onSave={v => patch({ product_value: v })} onDraftChange={editingFields ? v => setKF('product_value', v) : undefined} />
                        <TextField label="Currency" editing={editing && !readOnlyReason} value={ev('product_value_currency')} editValue={ev('product_value_currency')} disabled={busy || !!readOnlyReason} placeholder="USD" dirty={isDirty('product_value_currency')} onSave={v => patch({ product_value_currency: v })} onDraftChange={editingFields ? v => setKF('product_value_currency', v) : undefined} />
                    </div>
                </div>

                {/* Lost */}
                <div className="mt-5 pt-4 border-t border-stone-100">
                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Lost</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
                        <SelectField label="Reason Lost" editing={editing && !readOnlyReason} value={ev('reason_lost')} options={opt('reason_lost_options')} disabled={busy || !!readOnlyReason} dirty={isDirty('reason_lost')} onSave={v => patch({ reason_lost: v })} onDraftChange={editingFields ? v => setKF('reason_lost', v) : undefined} />
                        {ev('reason_lost') === 'Other' && (
                            <div className="sm:col-span-2">
                                <TextField label="Reason (Other)" editing={editing && !readOnlyReason} value={ev('reason_lost_other')} editValue={ev('reason_lost_other')} disabled={busy || !!readOnlyReason} dirty={isDirty('reason_lost_other')} onSave={v => patch({ reason_lost_other: v })} onDraftChange={editingFields ? v => setKF('reason_lost_other', v) : undefined} />
                            </div>
                        )}
                    </div>
                </div>
            </Card>

            {/* ── Campaign & Funnel ────────────────────────────────────────── */}
            <Card title="Campaign & Funnel">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
                    <SelectField label="Funnel" editing={editing && !readOnlyReason} value={ev('funnel')} options={opt('funnel_options')} disabled={busy || !!readOnlyReason} dirty={isDirty('funnel')} onSave={v => patch({ funnel: v })} onDraftChange={editingFields ? v => setKF('funnel', v) : undefined} />
                    <SelectField label="Page Variant" editing={editing && !readOnlyReason} value={ev('page_variant')} options={opt('page_variant_options')} disabled={busy || !!readOnlyReason} dirty={isDirty('page_variant')} onSave={v => patch({ page_variant: v })} onDraftChange={editingFields ? v => setKF('page_variant', v) : undefined} />
                    <SelectField label="Form Variant" editing={editing && !readOnlyReason} value={ev('form_variant')} options={opt('form_variant_options')} disabled={busy || !!readOnlyReason} dirty={isDirty('form_variant')} onSave={v => patch({ form_variant: v })} onDraftChange={editingFields ? v => setKF('form_variant', v) : undefined} />
                    <SelectField label="Product Variant" editing={editing && !readOnlyReason} value={ev('product_variant')} options={opt('product_variant_options')} disabled={busy || !!readOnlyReason} dirty={isDirty('product_variant')} onSave={v => patch({ product_variant: v })} onDraftChange={editingFields ? v => setKF('product_variant', v) : undefined} />
                </div>
            </Card>

            {/* ── UTM Acquisition ──────────────────────────────────────────── */}
            <Card title="UTM Acquisition">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
                    {UTM_KEYS.map(k => (
                        <UtmField key={k} label={humanize(k.replace('utm_', 'UTM '))}
                            value={c[k]} editing={editing && !readOnlyReason} disabled={busy || saving}
                            draft={editForm ? editForm[k] : utmDrafts[k]}
                            onDraftChange={editForm
                                ? v => setEditForm(p => ({ ...p, [k]: v }))
                                : v => setUtmDrafts(d => ({ ...d, [k]: v }))} />
                    ))}
                </div>
                {editing && !editForm && (
                    <div className="flex items-center gap-3 mt-4">
                        <button type="button"
                            className="text-xs font-medium px-3 py-1.5 rounded-md bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40 disabled:cursor-not-allowed"
                            onClick={saveUtms} disabled={busy || saving || !utmDirty}>
                            {busy ? 'Saving…' : 'Save UTM'}
                        </button>
                        {utmDirty && <span className="text-xs text-stone-400">Unsaved changes — click Save to commit</span>}
                    </div>
                )}
                <p className="text-xs text-stone-400 mt-3">{utmCaption}</p>
            </Card>
        </div>
    );
}